import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Signature } from "lucide-react";

export default function SenderIds() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { sender_id: "", type: "transactional", country: "IN", description: "", is_active: true };
  const [form, setForm] = useState(blank);

  const load = () => api.get("/sender-ids").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/sender-ids", form); toast.success("Sender ID created"); setOpen(false); setForm(blank); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this sender ID?")) return;
    await api.delete(`/sender-ids/${id}`); toast.success("Deleted"); load();
  };
  const toggle = async (item) => {
    await api.patch(`/sender-ids/${item.id}`, { ...item, is_active: !item.is_active }); load();
  };

  const StatusPill = ({ s }) => {
    const cls = s === "approved" ? "border-emerald-300 text-emerald-700"
      : s === "rejected" ? "border-red-300 text-red-700"
      : "border-amber-300 text-amber-700";
    return <Badge variant="outline" className={`rounded-sm text-[10px] ${cls}`}>{s}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="sender-ids-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">SMS</div>
          <h1 className="text-3xl font-black tracking-tighter">Sender IDs</h1>
          <p className="text-xs text-muted-foreground mt-1">Branded 3–11 character alphanumeric headers used on outbound SMS. Register with your DLT operator for India traffic.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-sm gap-2" data-testid="new-sender-id-button"><Plus className="h-4 w-4" /> New Sender ID</Button></DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>Register Sender ID</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Sender ID</Label><Input value={form.sender_id} onChange={e => setForm({ ...form, sender_id: e.target.value.toUpperCase() })} maxLength={11} placeholder="e.g. TZSNDS" className="rounded-sm font-mono uppercase" data-testid="sender-id-input" /></div>
              <div><Label>Type</Label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="h-9 w-full px-3 rounded-sm border border-border bg-background text-sm">
                  <option value="transactional">transactional</option>
                  <option value="promotional">promotional</option>
                  <option value="service">service</option>
                </select>
              </div>
              <div><Label>Country</Label>
                <select value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} className="h-9 w-full px-3 rounded-sm border border-border bg-background text-sm">
                  <option value="IN">India</option><option value="US">United States</option><option value="GB">United Kingdom</option><option value="AE">UAE</option><option value="SG">Singapore</option>
                </select>
              </div>
              <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-sm" data-testid="sender-desc" /></div>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-sm" data-testid="sender-create-button">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(s => (
          <Card key={s.id} className="rounded-sm shadow-none" data-testid={`sender-id-${s.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Signature className="h-4 w-4 text-muted-foreground" /><span className="font-mono font-bold tracking-wide">{s.sender_id}</span></div>
                <StatusPill s={s.status} />
              </div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.type} · {s.country}</div>
              <div className="text-xs">{s.description || "—"}</div>
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-2 text-xs"><Switch checked={s.is_active} onCheckedChange={() => toggle(s)} data-testid={`toggle-${s.id}`} /><span>{s.is_active ? "Active" : "Disabled"}</span></div>
                <Button variant="ghost" size="sm" className="rounded-sm text-red-600" onClick={() => del(s.id)} data-testid={`delete-sender-${s.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <Card className="rounded-sm shadow-none md:col-span-3"><CardContent className="text-center text-muted-foreground py-10">No sender IDs yet. Add one to send branded SMS.</CardContent></Card>}
      </div>
    </div>
  );
}
